import { Loader2, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { ConfidenceBadge } from "./ConfidenceBadge";
import { ScoreChip } from "./score-viz";

interface ReverseScoreResult {
  profileId: string;
  profileName: string;
  profileSlug: string;
  description: string | null;
  totalScore: number;
  confidence: "HIGH" | "MEDIUM" | "LOW";
  metricsScored: number;
  metricsTotal: number;
}

interface BestFitProfilesPanelProps {
  active: boolean;
  campaignId: string;
  locationId: string;
  topN?: number;
}

export function BestFitProfilesPanel({
  active,
  campaignId,
  locationId,
  topN = 3,
}: BestFitProfilesPanelProps) {
  const [results, setResults] = useState<ReverseScoreResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    if (!active || !locationId) return;

    let mounted = true;
    setLoading(true);
    setError(null);
    setSupported(true);
    setResults(null);

    fetch(`/api/campaigns/${campaignId}/locations/${locationId}/reverse-score`, {
      method: "POST",
    })
      .then(async (res) => {
        if (res.status === 400) {
          if (mounted) setSupported(false);
          return null;
        }
        if (!res.ok) throw new Error("Failed to fetch");
        return res.json();
      })
      .then((json) => {
        if (!mounted || !json) return;
        setResults((json.results ?? []) as ReverseScoreResult[]);
      })
      .catch(() => {
        if (mounted) setError("Failed to load best-fit profiles.");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [active, campaignId, locationId]);

  if (!supported) return null;

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3 mb-4">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="h-4 w-4 text-primary shrink-0" />
        <h3 className="text-sm font-semibold">Best-fit profiles</h3>
        <span className="text-[10px] text-muted-foreground ml-auto">
          ranked across multifamily sub-profiles
        </span>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      )}

      {error && !loading && <div className="py-2 text-xs text-destructive">{error}</div>}

      {!loading && !error && results && results.length === 0 && (
        <div className="py-2 text-xs text-muted-foreground">
          No score data available yet — run the campaign scoring job first.
        </div>
      )}

      {!loading && !error && results && results.length > 0 && (
        <ol className="space-y-1.5">
          {results.slice(0, topN).map((r, i) => (
            <li
              key={r.profileId}
              className="flex items-center gap-2 rounded-md border border-border/60 bg-background p-2"
            >
              <div className="flex items-center justify-center w-5 shrink-0">
                {i === 0 ? (
                  <Sparkles className="h-4 w-4 text-score-med" />
                ) : (
                  <span className="font-mono text-xs font-semibold tabular-nums text-muted-foreground">
                    {i + 1}
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate" title={r.profileName}>
                  {r.profileName}
                </div>
                {r.description && (
                  <div
                    className="text-[11px] text-muted-foreground line-clamp-2"
                    title={r.description}
                  >
                    {r.description}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <ScoreChip score={r.totalScore} />
                <ConfidenceBadge confidence={r.confidence} />
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
